/* eslint-disable react-hooks/rules-of-hooks */
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash-es';

import { ILocation } from '@rbi-ctg/frontend';
import { IServerOrder } from '@rbi-ctg/menu';
import { IRestaurant } from '@rbi-ctg/store';
import useMap from 'hooks/use-map';
import { MarkerTypes } from 'hooks/use-map/types';
import { getMarkerTypeForAlias } from 'hooks/use-map/utils';
import { useAuthContext } from 'state/auth';

interface IUseOrderConfirmationMap {
  serverOrder: IServerOrder;
  store: IRestaurant;
}

export default function useDriverLocation({
  serverOrder,
  store,
}: IUseOrderConfirmationMap): ReactNode {
  const [markerCoords, setMarkerCoords] = useState<Array<ILocation>>([]);
  const isDeliveryOrder = !!serverOrder.delivery;

  const destinationLatitude = serverOrder?.delivery?.dropoff?.latitude ?? 0;
  const destinationLongitude = serverOrder?.delivery?.dropoff?.longitude ?? 0;
  const pickupLatitude = serverOrder?.delivery?.pickup?.latitude ?? 0;
  const pickupLongitude = serverOrder?.delivery?.pickup?.longitude ?? 0;
  const { user } = useAuthContext();

  const mapCenter = useMemo(
    () =>
      isDeliveryOrder
        ? {
            lat: destinationLatitude,
            lng: destinationLongitude,
          }
        : { lat: store.latitude ?? 0, lng: store.longitude ?? 0 },
    [destinationLatitude, destinationLongitude, isDeliveryOrder, store.latitude, store.longitude]
  );

  const { map, createMarker, fitAndCenterFromCoords } = useMap({
    disableControls: false,
    position: mapCenter,
  });

  // Creates store & destination markers on map & updates map view to fit them.
  useEffect(() => {
    const mapCoords: ILocation[] = [];
    if (pickupLatitude && pickupLongitude) {
      const pickupCoords = { lat: pickupLatitude, lng: pickupLongitude };
      createMarker({
        id: 'pickupCoords',
        type: MarkerTypes.StoreOpenSelected,
        location: pickupCoords,
        onPress: () => null,
      });
      mapCoords.push(pickupCoords);
    }
    if (destinationLatitude && destinationLongitude) {
      const destinationCoord = { lat: destinationLatitude, lng: destinationLongitude };

      let markerType = MarkerTypes.Destination;

      if (isDeliveryOrder) {
        // TODO: we may want to return the alias from the server order.
        const deliveryAddressAlias = user?.details?.deliveryAddresses?.find(
          address => address.addressLine1 === serverOrder?.delivery?.dropoff?.addressLine1
        )?.alias;

        markerType = getMarkerTypeForAlias(deliveryAddressAlias ?? undefined);
      }

      createMarker({
        id: 'destinationCoord',
        type: markerType,
        location: destinationCoord,
        onPress: () => null,
      });
      mapCoords.push(destinationCoord);
    }
    fitAndCenterFromCoords(mapCoords);
    if (!isEqual(markerCoords, mapCoords)) {
      setMarkerCoords(mapCoords);
    }
  }, [
    createMarker,
    destinationLatitude,
    destinationLongitude,
    fitAndCenterFromCoords,
    isDeliveryOrder,
    mapCenter,
    markerCoords,
    pickupLatitude,
    pickupLongitude,
    serverOrder?.delivery?.dropoff?.addressLine1,
    user?.details.deliveryAddresses,
  ]);
  return map;
}
